import { createFileRoute, Link } from '@tanstack/react-router'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  Building02Icon,
  UserGroupIcon,
  UserCircleIcon,
  CheckmarkCircle01Icon,
} from '@hugeicons/core-free-icons'
import { AuthGuard } from '@/features/auth'
import { useAdminPlatformStats } from '@/features/admin/services/admin.convex'
import { OverviewCard } from '@/components/common/overview-card'
import { Badge } from '@/components/ui/badge'

export const Route = createFileRoute('/(dashboard)/admin/')({
  component: RouteComponent,
  beforeLoad: () => ({
    title: 'Admin — Dashboard',
    breadcrumbs: [{ title: 'Admin' }, { title: 'Dashboard' }],
  }),
})

const billingStatusVariant: Record<
  string,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  active: 'default',
  unpaid: 'secondary',
  cancelled: 'outline',
  expired: 'destructive',
  past_due: 'destructive',
  paused: 'secondary',
}

function RouteComponent() {
  const { data: stats } = useAdminPlatformStats()

  return (
    <AuthGuard requiresPlatformAdmin>
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex items-center justify-between p-6 bg-muted/30 rounded-lg">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">
              Platform Overview
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Real-time snapshot of all organizations and users on NextPeak.
            </p>
          </div>
        </div>

        {/* Key metrics */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6 xl:grid-cols-4 2xl:gap-7.5">
          <Link to="/admin/organizations" className="block h-full">
            <OverviewCard
              title="Customer Orgs"
              total={stats?.totalOrganizations ?? '—'}
              icon={
                <HugeiconsIcon
                  icon={Building02Icon}
                  className="h-6 w-6 text-primary"
                  strokeWidth={1.5}
                />
              }
            />
          </Link>
          <Link to="/admin/users" className="block h-full">
            <OverviewCard
              title="Total Users"
              total={stats?.totalUsers ?? '—'}
              icon={
                <HugeiconsIcon
                  icon={UserGroupIcon}
                  className="h-6 w-6 text-primary"
                  strokeWidth={1.5}
                />
              }
            />
          </Link>
          <OverviewCard
            title="Platform Admins"
            total={stats?.platformAdmins ?? '—'}
            icon={
              <HugeiconsIcon
                icon={UserCircleIcon}
                className="h-6 w-6 text-primary"
                strokeWidth={1.5}
              />
            }
          />
          <OverviewCard
            title="Total Paid Seats"
            total={stats?.totalPaidSeats ?? '—'}
            icon={
              <HugeiconsIcon
                icon={CheckmarkCircle01Icon}
                className="h-6 w-6 text-primary"
                strokeWidth={1.5}
              />
            }
          />
        </div>

        {/* Billing breakdown */}
        <div className="p-6 bg-muted/30 rounded-lg space-y-4">
          <h2 className="text-base font-semibold text-foreground">
            Subscription Breakdown
          </h2>
          {stats ? (
            <div className="flex flex-wrap gap-3">
              {Object.entries(stats.billingBreakdown).map(([status, count]) => (
                <div
                  key={status}
                  className="flex items-center gap-2 px-3 py-2 bg-background rounded-lg border"
                >
                  <Badge variant={billingStatusVariant[status] ?? 'secondary'}>
                    {status.replace('_', ' ')}
                  </Badge>
                  <span className="text-sm font-semibold">{count}</span>
                  <span className="text-xs text-muted-foreground">
                    org{count !== 1 ? 's' : ''}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex gap-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <div
                  key={i}
                  className="h-10 w-28 bg-muted animate-pulse rounded-lg"
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </AuthGuard>
  )
}
